import { useState } from "react";
import { useParams } from "react-router-dom";
import { BackButton } from "../../../../components/buttons/backButton";
import { Button } from "../../../../components/buttons/button";
import { ProfileIcon } from "../../../../components/cards/profilePic";
import Dropdown from "../../../../components/dropdown/dropdown";
import { EmptyState } from "../../../../components/empty";
import { EventImage } from "../../../../components/event/eventImage";
import { Icon } from "../../../../components/icon/icon";
import { SearchInput } from "../../../../components/inputs/searchInput";
import Pagination from "../../../../components/pagination";
import { Pill } from "../../../../components/pill";
import { BgWrapper } from "../../../../components/wrapper/bgWrapper";
import { useGetOrganizersEventsQuery } from "../../../../redux/features/sales/sales.api";
import { formatDate, formatToCurrency } from "../../../../utils/helpers";
import { QueryParams } from "../../../../utils/types";

export const SingleOrganizerPage = () => {
  const { id } = useParams<{ id: string }>();
  const [query, setQuery] = useState<QueryParams>({
    page: 1,
    limit: 50,
    search: undefined,
    sortOrder: "desc",
  });
  const [year, setYear] = useState<string>("All Time");

  const dropDownOptions = [
    {
      label: "All Time",
      value: 2025,
      onClick: () => {
        setYear("All Time");
      },
    },
  ];

  const { isFetching: fetchingEvenntDetails, data: eventsData } =
    useGetOrganizersEventsQuery(
      {
        id,
        ...query,
        search: query.search === "" ? undefined : query.search,
      },
      {
        refetchOnMountOrArgChange: true,
      },
    );

  const organizer = eventsData?.data?.data?.[0]?.createdBy;

  return (
    <div className="mx-auto flex w-full max-w-[700px] flex-col gap-[2px]">
      <BackButton />
      <BgWrapper
        className="mt-[6px]"
        loading={fetchingEvenntDetails}
        loaderHeight={202}
      >
        <div className="flex flex-wrap gap-5 py-5">
          <ProfileIcon dimension={64} className="!p-3" />
          <div className="flex-1">
            <p className="text-2xl font-bold text-grey-900">
              {organizer?.firstName} {organizer?.lastName}
            </p>
            <div className="mt-[2px] flex items-center gap-1">
              <Icon svg="email-plain" width={16} height={16} className="mb-1" />
              <p className="text-base font-bold text-grey-500">
                {organizer?.email}
              </p>
            </div>
            <div className="mt-[2px] flex items-center gap-1">
              <Icon svg="phone-plain" width={16} height={16} className="mb-1" />
              <p className="text-base font-bold text-grey-500">
                {organizer?.phoneNumber || "No phone number"}
              </p>
            </div>
            <div className="mt-[2px] flex items-center gap-1">
              <Icon
                svg="calendar-plain"
                width={16}
                height={16}
                className="mb-1"
              />
              <p className="text-base font-bold text-grey-500">
                Joined on {formatDate(organizer?.createdAt)}
              </p>
            </div>
          </div>
          <Pill
            text={`${organizer?.status === "active" ? "ACTIVE" : "NO EVENT"}`}
            variation={`${organizer?.status === "active" ? "green" : "grey"}`}
          />
        </div>
      </BgWrapper>

      {!fetchingEvenntDetails && (
        <BgWrapper>
          <div className="flex w-full flex-col items-center justify-between gap-4 md:flex-row">
            <SearchInput
              searchFunction={(value) => {
                setQuery({
                  ...query,
                  search: value,
                  page: 1,
                });
              }}
              placeholder="Search"
              className="md:!max-w-[224px]"
            />

            <div className="flex w-full max-w-fit items-center">
              <Dropdown
                menuItems={dropDownOptions.map((option) => {
                  return {
                    name: (
                      <p className="soft-shrink flex items-center gap-2 !rounded-md !bg-white p-3 text-left !text-sm !font-bold !text-grey-950">
                        {option.label}
                      </p>
                    ),
                    onClick: option.onClick,
                  };
                })}
                className=""
                menuClassName="!gap-1 !border-0 !bg-grey-50 !p-1 !w-[200px]"
                menuItemsClassName="border-none !text-base !bg-transparent !p-0"
                position="bottom end"
              >
                <Button
                  text={year}
                  icon="caret-down"
                  type="button"
                  iconFirst={false}
                  variation="styled-grey"
                  iconClassName={"mb-1"}
                  className="max-w-fit"
                />
              </Dropdown>
            </div>
          </div>
        </BgWrapper>
      )}

      {fetchingEvenntDetails
        ? [1, 2].map((v) => {
            return <BgWrapper key={`${v}`} loaderHeight={184} loading />;
          })
        : undefined}

      <>
        {!fetchingEvenntDetails &&
          eventsData?.data?.data?.map((event) => (
            <BgWrapper
              key={event._id}
              className={`flex flex-col gap-8 md:flex-row md:flex-wrap md:items-center xl:items-start`}
            >
              <EventImage
                image={event.image}
                dimension={158}
                className={"!order-last"}
              />
              <div className="flex w-full flex-col justify-center gap-4 md:min-w-[350px] md:flex-1 xl:!min-h-full xl:w-fit">
                <Pill
                  text={event.status === "upcoming" ? "UPCOMING" : "NO EVENT"}
                  variation={event.status === "upcoming" ? "blue" : "grey"}
                />
                <p className="font-fraunces text-[24px] text-primary">
                  {event.name}
                </p>
                <div className="flex flex-col gap-[10px] text-sm text-grey-900">
                  {(event?.startDate || event.endDate) && (
                    <p className="flex items-center gap-2">
                      <Icon
                        svg="calendar"
                        width={20}
                        height={20}
                        className="min-w-fit text-grey-500"
                      />
                      {formatDate(event?.startDate, "ddd, MMMM DD, YYYY", true)}{" "}
                      {formatDate(
                        event?.startDate,
                        "ddd, MMMM DD, YYYY",
                        true,
                      ) ===
                      formatDate(event?.endDate, "ddd, MMMM DD, YYYY", true)
                        ? ""
                        : `- ${formatDate(event?.endDate, "ddd, MMMM DD, YYYY", true)}`}
                    </p>
                  )}

                  {(event.startTime || event?.endTime) && (
                    <p className="flex items-center gap-2">
                      <Icon
                        svg="clock"
                        width={20}
                        height={20}
                        className="min-w-fit text-grey-500"
                      />
                      {event?.startTime} - {event?.endTime}{" "}
                    </p>
                  )}
                  <p className="flex items-center gap-2">
                    <Icon
                      svg="ticket"
                      width={20}
                      height={20}
                      className="min-w-fit text-grey-500"
                    />
                    {event.totalTicketsSold > 0
                      ? `${event.totalTicketsSold} Ticket${event.totalTicketsSold !== 1 ? "s" : ""} Sold`
                      : "No Tickets Sold"}{" "}
                    ∙{" "}
                    {formatToCurrency(event?.totalCommission ?? 0, {
                      currency: "NGN",
                    })}{" "}
                    earned
                  </p>
                </div>
              </div>
            </BgWrapper>
          ))}
      </>

      <BgWrapper>
        <Pagination
          currentPage={query.page!}
          totalCount={eventsData?.data?.totalCount ?? 0}
          itemsPerPage={query.limit!}
          onPageChange={(page) => {
            setQuery({
              ...query,
              page: page,
            });
          }}
          containerClassName="!px-0 !py-0"
          showPagNumbers={false}
        />
      </BgWrapper>
      {!fetchingEvenntDetails &&
      [0, undefined].includes(eventsData?.data?.data?.length) ? (
        <BgWrapper>
          <EmptyState
            text="No Events Created"
            subtext={`All events created by ${organizer?.firstName} would show here`}
            image="/assets/images/calendar-3d.png"
          />
        </BgWrapper>
      ) : undefined}
    </div>
  );
};
